/**
 * woLogRecon — BQC2 reconciliation: what the work-order log says was planned
 * to be cut, against what has actually been scanned cut-ready on the floor.
 *
 * The structural twin of receivingLedger's DS ↔ DR reading, one end of the
 * pipeline earlier. There the unit is a dispatch slip and the question is
 * "how much of what went OUT has come BACK"; here the unit is a WORK ORDER
 * and the question is "how much of what BQC1 planned has actually come off
 * the cutting table". Same derived-every-time posture, and for the same
 * reason: a status field on a work order can drift from the scans that are
 * supposed to justify it, a sum over the scan log cannot.
 *
 * THE SCAN LOG IS THE TRUTH, THE WORKBOOK IS THE CLAIM. iSyn2_CutReadyScans
 * is app-owned intake — a bundle enters it when somebody physically holds it
 * under the scanner. iSyn_WIPs.currentStage is Excel-owned, written by the
 * periodic workbook import, and can say BQC3 for a bundle nobody ever
 * scanned here (a catch-up import, a hand-edited cell, a WIP cut before this
 * station existed). Neither is wrong — they answer different questions — so
 * this module reports BOTH and names the disagreement (see cutLogState's
 * "unlogged") rather than picking a winner.
 *
 * A work order is identified by its JO SERIES, falling back to the WK series
 * and then the batch code, because BQC1 does not always carry all three: the
 * fallback ladder is what keeps a WIP from landing in an "(unassigned)"
 * bucket that reconciles against nothing. Every figure below is by the SET as
 * well as by the WIP — a work order can be short a few sets inside a bundle,
 * not just short whole bundles, which is exactly the shortfall a bundle-only
 * count hides.
 */
import type { IProductionWip, CurrentStage } from "../../lib/sp/ProductionGridService";
import type { ICutReadyScan } from "../../lib/sp/WorkcentersService";

/**
 * The pipeline, in the order a WIP actually moves through it.
 *
 * This ordering is the whole basis of the reconciliation below: a bundle
 * cannot be dispatched before it is cut, received before it is dispatched, or
 * QC'd before it is received. So evidence at ANY stage is evidence of every
 * stage before it, and the furthest stage with evidence is the WIP's real
 * position — regardless of which sheets in between happen to have a row for
 * it.
 *
 * The workbook parser already reasons this way on its own side of the fence
 * (parseProductionGridWorkbook caps cutQty >= assignedQty >= receivedQty >=
 * qcQty and lets "the earliest downstream scan date stand in for a missing
 * upstream scan"). It just never persists the cut figure it derives, so this
 * screen has to reach the same conclusion from the per-stage facts that DO
 * reach iSyn_WIPs.
 */
export const PIPELINE = ["BQC1", "BQC2", "BQC3", "BQC4", "BQC5"] as const;
export type PipelineStage = (typeof PIPELINE)[number];

/** Where the proof that a bundle was cut comes from — this station's own scan
 * log, or the furthest downstream stage that has a record of it. */
export type CutEvidence = "scan" | PipelineStage | "none";

/** Stages that mean "this WIP is at or past the cutting table", per the
 * workbook's own currentStage. BQC1 is the only stage genuinely before it;
 * DONE is terminal and can only be reached by having been cut first. LOST and
 * DAMAGED are deliberately absent — a bundle can be written off before it ever
 * reaches the table, so neither is evidence of cutting. */
const CUT_OR_LATER: ReadonlySet<string> = new Set<CurrentStage>([
  "BQC2", "BQC3", "BQC4", "BQC5", "DONE",
]);

/**
 * The furthest stage this WIP has reached, read downstream-first.
 *
 * Each rung looks for a fact only that stage produces, so a missing sheet row
 * upstream cannot drag the answer back. currentStage is consulted as well as
 * the raw facts because it is the parser's own cascade and sometimes knows
 * things the individual columns do not — but never on its own where a raw fact
 * disagrees, since it is one derived field and these are the evidence.
 *
 * NOT evidence, and the trap worth naming: `dateAssignedToCutter` is BQC1's
 * record of handing fabric TO a cutter. It is the moment cutting was
 * scheduled, not the moment it happened, and reading it as BQC2 would mark
 * every planned bundle cut.
 */
export function furthestStageOf(wip: IProductionWip): PipelineStage {
  const stage = wip.currentStage ?? "";
  // BQC5 — QC has passed sets, or stamped a date, or the WIP is DONE.
  if ((wip.qcPassedQty ?? 0) > 0 || wip.qcPassedAt || stage === "BQC5" || stage === "DONE") return "BQC5";
  // BQC4 — something came back: sets received, a receipt date, or the
  // clerk's own Goods Receipt Number against it.
  if ((wip.receivedQty ?? 0) > 0 || wip.receivedAt || wip.grn || stage === "BQC4") return "BQC4";
  // BQC3 — it went out: a dispatch date or a DS number. Deliberately NOT
  // assignedSewer, which cascades all the way back to BQC1's hand-kept
  // planning column (see resolveAssignedSewer) and so can name a sewer for a
  // bundle that has never left the building.
  if (wip.dispatchedAt || (wip.assignedDS ?? "").trim() || stage === "BQC3") return "BQC3";
  // BQC2 — the CUTTING sheet itself has a row for it.
  if (CUT_OR_LATER.has(stage)) return "BQC2";
  return "BQC1";
}

/**
 * Where the proof that this bundle was cut comes from, strongest first.
 *
 * This station's own scan wins when there is one — it is the only per-set,
 * timestamped record. Otherwise the furthest downstream stage with evidence
 * stands in for it, which is the fallback the floor actually relies on: a
 * bundle sitting at BQC4 was self-evidently cut, whether or not anyone
 * scanned it here or the CUTTING sheet ever got a row.
 */
export function cutEvidenceOf(wip: IProductionWip, scannedHere: boolean): CutEvidence {
  if (scannedHere) return "scan";
  const furthest = furthestStageOf(wip);
  return furthest === "BQC1" ? "none" : furthest;
}

/** The active (non-undone) scans only — an undone scan did not happen. Same
 * rule receivingLedger and qcLedger apply to their own logs. */
export function activeCutScans(scans: readonly ICutReadyScan[]): ICutReadyScan[] {
  return scans.filter(s => !s.undoneAt);
}

/** Newest active scan per WIP, by scan time with the higher list id breaking
 * a tie — the row the station shows and offers to undo. */
export function latestCutScanByWip(scans: readonly ICutReadyScan[]): Map<string, ICutReadyScan> {
  const out = new Map<string, ICutReadyScan>();
  for (const s of activeCutScans(scans)) {
    const id = (s.wipId ?? "").trim();
    if (!id) continue;
    const prior = out.get(id);
    if (!prior) { out.set(id, s); continue; }
    const newer = s.scannedAt > prior.scannedAt
      || (s.scannedAt === prior.scannedAt && s.spId > prior.spId);
    if (newer) out.set(id, s);
  }
  return out;
}

/**
 * Where one WIP stands between the two records.
 *
 *  - `cut`      — scanned here. The floor and the log agree, or the log has
 *                 simply not caught up yet, which is the normal lag.
 *  - `unlogged` — the workbook already has it at or past BQC2, but this
 *                 station never scanned it. The one state worth chasing:
 *                 either it was cut before the app existed / outside the
 *                 station, or the workbook's stage is wrong.
 *  - `awaiting` — planned, still at BQC1, never scanned. Work still to do.
 */
export type CutLogState = "cut" | "unlogged" | "awaiting";

export function cutLogState(wip: IProductionWip, scanned: boolean): CutLogState {
  if (scanned) return "cut";
  // cutSomewhere, NOT workbookHasCut: the two ask different questions and
  // mixing them up is what let a WIP report 2 of 2 sets cut while its own
  // state said "awaiting". This one must agree with the set arithmetic.
  return cutSomewhere(wip) ? "unlogged" : "awaiting";
}

/**
 * Does anything downstream prove this bundle was cut, scan or no scan?
 *
 * Broader than workbookHasCut's currentStage read: this also catches a WIP
 * whose currentStage never caught up but which plainly has a DS number, a
 * receipt or a QC pass against it.
 */
export function cutSomewhere(wip: IProductionWip): boolean {
  return furthestStageOf(wip) !== "BQC1";
}

/**
 * Does the workbook's own stage already put this WIP at or past cutting?
 *
 * NARROWER THAN cutSomewhere ON PURPOSE, and the difference matters. This
 * reads only currentStage, the parser's single derived field; cutSomewhere
 * also weighs the raw per-stage facts and so catches a WIP whose stage never
 * caught up.
 *
 * The one caller is cuttingOutbox's excludeAlreadyCutInWorkbook, which asks
 * "would exporting this duplicate a record the workbook already holds". The
 * conservative read is the right one there: over-excluding leaves a gap
 * somebody can still fill by hand, whereas over-including writes rows into a
 * file another team works from every day.
 */
export function workbookHasCut(wip: IProductionWip): boolean {
  return CUT_OR_LATER.has(wip.currentStage ?? "");
}

export interface ICutTally {
  wipId: string;
  /** The WIP's declared sets — what the work order is owed. */
  qty: number;
  /** Sets accounted for as cut. This station's scans when it has them;
   * otherwise the WHOLE bundle whenever anything downstream proves it was cut
   * — see the note on cutTally. Capped at qty. */
  cut: number;
  /** Sets THIS STATION has scanned, ignoring anything downstream. What the
   * scan panel needs: `cut` can be the full bundle on a downstream bundle
   * nobody here has logged, and offering "nothing left to record" for one of
   * those would block the very catch-up scan the station exists to take. */
  scannedSets: number;
  /** qty − cut. */
  awaiting: number;
  /** Every declared set is accounted for by a scan. */
  complete: boolean;
  state: CutLogState;
  /** Which record vouches for the cut — this station's scan, or the furthest
   * downstream stage standing in for it. "none" when nothing does. */
  evidence: CutEvidence;
  /** Most recent active scan for this WIP, if any. */
  lastScan?: ICutReadyScan;
}

/**
 * How much of one bundle is cut, and on whose word.
 *
 * THE WHOLE BUNDLE, not a part of it, once anything downstream has it. A
 * bundle is cut as a unit and then moves on as a unit: if BQC4 received two of
 * its four sets, all four were cut and dispatched — two have simply not come
 * back yet. Counting only the received two would report the other two as still
 * awaiting the cutting table, which is the opposite of true, and is what made
 * the WO log recon panel read "Nothing cut yet · 709 sets outstanding" over
 * work orders whose bundles were sitting at QC.
 *
 * A scan here still wins when there is one, because it is the only per-set
 * record: a bundle half-scanned at this station and not yet anywhere
 * downstream is genuinely half cut, and saying so is the point of the station.
 * Downstream evidence overrides that only once it exists, at which point the
 * remainder demonstrably got cut too.
 */
export function cutTally(wip: IProductionWip, scans: readonly ICutReadyScan[]): ICutTally {
  const qty = Math.max(0, Number(wip.qty) || 0);
  const mine = activeCutScans(scans).filter(s => (s.wipId ?? "").trim() === wip.wipId);
  const summed = mine.reduce((n, s) => n + (Number(s.qty) > 0 ? Number(s.qty) : qty), 0);
  const scannedHere = mine.length > 0;
  const downstream = cutSomewhere(wip);
  const scannedSets = Math.min(qty, Math.max(0, summed));
  const cut = downstream ? qty : scannedSets;
  const lastScan = latestCutScanByWip(mine).get(wip.wipId);
  return {
    wipId: wip.wipId,
    qty,
    cut,
    scannedSets,
    awaiting: Math.max(0, qty - cut),
    // A zero-qty WIP is never "complete" off the back of arithmetic alone —
    // 0 of 0 is a row with no declared sets, not a finished one. It counts as
    // complete only once something actually vouches for it.
    complete: qty > 0 ? cut >= qty : (scannedHere || downstream),
    state: cutLogState(wip, scannedHere),
    evidence: cutEvidenceOf(wip, scannedHere),
    lastScan,
  };
}

/**
 * One WIP per WIP NUMBER, keeping the copy furthest down the pipeline.
 *
 * wipId is not reliably unique in iSyn_WIPs — a double insert during import
 * leaves two items for one number, as ISewerViewWip's own note records. Every
 * figure derived per WIP double-counts those bundles until they are collapsed:
 * on the live board 26051486, 25120640 and 25111413 were each appearing twice,
 * inflating both the Cutting screen's own counts and this module's
 * per-work-order totals.
 *
 * Furthest-along wins so a stale duplicate cannot drag a bundle's state
 * backwards, with the higher spId breaking a tie so the choice is stable
 * between renders.
 */
export function dedupeWips(wips: readonly IProductionWip[]): IProductionWip[] {
  const best = new Map<string, IProductionWip>();
  for (const w of wips) {
    const id = (w.wipId ?? "").trim();
    if (!id) continue;
    const prior = best.get(id);
    if (!prior) { best.set(id, w); continue; }
    const rank = (x: IProductionWip): number => PIPELINE.indexOf(furthestStageOf(x));
    if (rank(w) > rank(prior) || (rank(w) === rank(prior) && w.spId > prior.spId)) best.set(id, w);
  }
  return [...best.values()];
}

// ─── work orders ────────────────────────────────────────────────────────────

/**
 * Placeholders the workbook uses to mean "no work order", which must never
 * become one.
 *
 * `0` is the live risk, and not a hypothetical one: AutoSyncBQC's lookup
 * columns write 0 for not-found by design (`XLOOKUP(…,0,0)`), so a WIP whose
 * JO SERIES was never resolved carries a literal "0". Read as a work order it
 * collects every such WIP into one enormous bucket that reconciles against
 * nothing — 8,921 of 15,573 rows on the live site sat under a card labelled
 * "0" before this existed.
 *
 * Same list and same reasoning as parseProductionGridWorkbook's own
 * NOT_A_SEWER, which exists to stop the identical 0 from becoming a sewer
 * called "0". Duplicated rather than imported: that set is private to the
 * production-grid module, and this one must not reach into it.
 */
const NOT_A_WORK_ORDER: ReadonlySet<string> = new Set(
  ["", "0", "-", "--", "—", "#N/A", "N/A", "NA", "NONE", "NULL", "FALSE"]
);

/** The trimmed value, or "" when it is one of the workbook's not-found
 * placeholders. Case-insensitive, since the sheet is inconsistent about it. */
function realValue(v: string | undefined): string {
  const t = (v ?? "").trim();
  return NOT_A_WORK_ORDER.has(t.toUpperCase()) ? "" : t;
}

/**
 * A series with its punctuation and casing taken off, so two SPELLINGS of one
 * work order can be told apart from two different work orders.
 *
 * The workbook writes the same WK series both ways: BQC1's WK SERIES column
 * holds "WK05150821" while the batch code for the very same rows reads
 * "WK 0515-0821". Compared verbatim those are two work orders, which is how
 * the panel ended up printing both of them side by side on one card.
 */
export function seriesKey(v: string): string {
  return v.toUpperCase().replace(/[^A-Z0-9]+/g, "");
}

/**
 * Of two spellings of ONE series, the one to put on screen.
 *
 * The separated form wins — "WK 0515-0821" over "WK05150821" — because the
 * groups are what a person reads: the week and the order range are visible in
 * one and run together in the other. Length then alphabetical only decide ties
 * nobody will ever see; they are there so the label a card shows does not
 * depend on which WIP happened to be read first.
 */
function bestSpelling(a: string, b: string): string {
  const gaps = (s: string): number => s.length - seriesKey(s).length;
  if (gaps(a) !== gaps(b)) return gaps(a) > gaps(b) ? a : b;
  if (a.length !== b.length) return a.length > b.length ? a : b;
  return a <= b ? a : b;
}

/** The rungs of the work-order ladder that this WIP actually carries. */
function workOrderRungs(wip: IProductionWip): string[] {
  return [wip.joSeries, wip.wkSeries, wip.batchCode, wip.orderId]
    .map(realValue)
    .filter(v => v.length > 0);
}

/**
 * The work order a WIP belongs to.
 *
 * JO SERIES first — it is the number the floor's own paperwork carries. WK
 * series next, then the batch code, then the order id: BQC1 fills these in
 * inconsistently, and a WIP that falls all the way through would otherwise
 * pile into one meaningless bucket. Blank on a WIP that carries none of them,
 * so the caller can keep it out of the panel rather than invent a work order
 * for it.
 *
 * Each rung is checked against NOT_A_WORK_ORDER rather than merely for
 * emptiness, so a not-found "0" falls THROUGH to the next rung instead of
 * stopping the ladder — which is what turns those rows into their real batch
 * code rather than a card called "0".
 *
 * WHICH rung wins is decided by the ladder; how the winner is SPELLED is not.
 * Once the rung is chosen, every other rung naming the same series (see
 * seriesKey) is a candidate for the label, and the most readable of them is
 * what comes back. So a WIP whose WK SERIES is "WK05150821" and whose batch
 * code is "WK 0515-0821" reads as one work order, spelled the way the batch
 * code spells it, instead of as its own identity with its own twin beside it.
 */
export function workOrderOf(wip: IProductionWip): string {
  const rungs = workOrderRungs(wip);
  const first = rungs[0];
  if (!first) return "";
  const key = seriesKey(first);
  return rungs.filter(r => seriesKey(r) === key).reduce(bestSpelling);
}

/**
 * The WK series or batch code to show BESIDE a work order — only when it is a
 * genuinely different series.
 *
 * Empty when the WIP's other columns are just the same series written another
 * way, which is the common case and the reason this exists: printing both is
 * noise, and the noise reads as two identifiers to reconcile when there is
 * only one.
 */
export function secondarySeriesOf(wip: IProductionWip, wo: string): string {
  const key = seriesKey(wo);
  return [wip.wkSeries, wip.batchCode]
    .map(realValue)
    .find(v => v.length > 0 && seriesKey(v) !== key) ?? "";
}

export interface IWoLogRow {
  /** The work order's identity — see workOrderOf. */
  wo: string;
  /** The WK series / batch code shown beside it, when it names a DIFFERENT
   * series — see secondarySeriesOf. Empty rather than the same series spelled
   * another way, which is what put "WK05150821  WK 0515-0821" on one card. */
  batch: string;
  /** Bundles on this work order. */
  total: number;
  /** Bundles cut, on ANY evidence — scanned here, or proven by a downstream
   * stage that could not have been reached uncut. */
  cut: number;
  /** The subset of `cut` this station has no scan for: cutting is done, the
   * BQC2 record of it is missing. Counted inside `cut`, not beside it — the
   * work is finished either way; what is outstanding is the paperwork. */
  unlogged: number;
  /** Declared sets across every bundle. */
  plannedSets: number;
  /** Sets actually accounted for by scans. */
  cutSets: number;
  /** plannedSets − cutSets. */
  outstandingSets: number;
  /** Every bundle scanned, nothing unlogged. */
  reconciled: boolean;
  /** Most recent scan across the work order, ISO — undefined if none. */
  lastCutAt?: string;
  /** Earliest due date across the work order, ISO — undefined if none. */
  dueDate?: string;
}

/**
 * One row per work order, planned against cut.
 *
 * `reconciled` deliberately requires unlogged === 0 as well as cut === total.
 * Every bundle being cut is not the same as this station having the record:
 * a work order whose bundles are provably cut but which BQC2 never logged is
 * precisely the case the panel exists to surface, and letting it read
 * RECONCILED would hide it behind the very badge somebody uses to decide they
 * can stop looking. So a fully-cut work order with gaps still reads PENDING,
 * with nothing outstanding and its unlogged count named.
 */
export function woLogRows(
  wips: readonly IProductionWip[],
  scans: readonly ICutReadyScan[]
): IWoLogRow[] {
  const scanned = latestCutScanByWip(scans);
  // Indexed once, not re-filtered per WIP. The shop-wide WIP list runs to
  // thousands of rows and the scan log to thousands more, so handing
  // cutTally the whole log for every WIP is a quadratic sweep that shows up
  // as a visibly frozen screen on the real dataset.
  const byWip = new Map<string, ICutReadyScan[]>();
  for (const s of scans) {
    const id = (s.wipId ?? "").trim();
    if (!id) continue;
    const list = byWip.get(id);
    if (list) list.push(s); else byWip.set(id, [s]);
  }
  const byWo = new Map<string, IWoLogRow>();

  // Collapsed first — otherwise a double-inserted WIP counts twice in this
  // work order's own bundle and set totals.
  for (const w of dedupeWips(wips)) {
    const wo = workOrderOf(w);
    if (!wo) continue;
    // Keyed on the series itself, not on how this WIP spells it, so a batch
    // whose rows disagree about the punctuation is ONE card rather than two
    // half-counted ones. The card then shows the most readable spelling any
    // of its rows carries — see bestSpelling.
    const key = seriesKey(wo);
    let row = byWo.get(key);
    if (!row) {
      row = {
        wo, batch: secondarySeriesOf(w, wo), total: 0, cut: 0, unlogged: 0,
        plannedSets: 0, cutSets: 0, outstandingSets: 0, reconciled: false,
      };
      byWo.set(key, row);
    } else {
      row.wo = bestSpelling(row.wo, wo);
      if (!row.batch) row.batch = secondarySeriesOf(w, wo);
    }
    const t = cutTally(w, byWip.get(w.wipId) ?? []);
    row.total += 1;
    row.plannedSets += t.qty;
    row.cutSets += t.cut;
    // "cut" counts every bundle that IS cut, however we know. unlogged is a
    // provenance note inside that figure rather than a rival to it, so
    // cut + awaiting always adds up to total.
    if (t.state !== "awaiting") row.cut += 1;
    if (t.state === "unlogged") row.unlogged += 1;

    const at = scanned.get(w.wipId)?.scannedAt;
    if (at && (!row.lastCutAt || at > row.lastCutAt)) row.lastCutAt = at;
    const due = (w.dueDate ?? "").trim();
    if (due && (!row.dueDate || due < row.dueDate)) row.dueDate = due;
  }

  for (const row of byWo.values()) {
    row.outstandingSets = Math.max(0, row.plannedSets - row.cutSets);
    row.reconciled = row.total > 0 && row.cut === row.total && row.unlogged === 0;
  }
  return [...byWo.values()];
}

/**
 * Descending throughout, so the newest work order is the top card: most
 * recently cut first, then by due date, then by work order.
 *
 * Deliberately NOT grouped by status any more. It used to float pending work
 * orders above reconciled ones, which put a card's badge ahead of its date and
 * made the panel's order impossible to predict from what was on screen — the
 * live board was running 288, 275, 235, 192 unlogged down the page, which
 * reads as arbitrary unless you know the rule. The RECONCILED / PENDING badge
 * still says which is which on every card, and the search box still finds a
 * work order by number, so nothing is lost by ordering on the dates people
 * actually recognise. Matches the WIP table beside it, which orders the same
 * way.
 */
export function sortWoLogRows(rows: readonly IWoLogRow[]): IWoLogRow[] {
  return [...rows].sort((a, b) => {
    const ac = a.lastCutAt ?? "", bc = b.lastCutAt ?? "";
    if (ac !== bc) return ac && bc ? bc.localeCompare(ac) : ac ? -1 : 1;
    const ad = a.dueDate ?? "", bd = b.dueDate ?? "";
    if (ad !== bd) return ad && bd ? bd.localeCompare(ad) : ad ? -1 : 1;
    return b.wo.localeCompare(a.wo);
  });
}
